import {
    QueryFunctionContext,
    InfiniteData,
    // eslint-disable-next-line no-restricted-imports
    useInfiniteQuery as _useInfiniteQuery,
    UseInfiniteQueryOptions,
    QueryKey,
} from "@tanstack/react-query";
import { HttpMethod, THttpMethod } from "src/constants/http-method";
import { ILocale } from "src/constants/locale";
import { MAX_ATTEMPTS, RETRY_DELAY_MS } from "src/constants/retry";
import { buildQueryUrl } from "src/utils/query-util";
import { useRouterLocale } from "src/utils/router/use-router-locale";

type TBodyGenerator = (pageParam: number) => string;

interface IQueryFn {
    _url: string;
    locale: ILocale;
    queryParams?: URLSearchParams;
    pageParamOptions?: TPageParamOptions;
    body?: string | TBodyGenerator;
    method?: THttpMethod;
}

const getQueryFn =
    <Data>({ _url, locale, queryParams, pageParamOptions, body, method }: IQueryFn) =>
    async ({ pageParam }: QueryFunctionContext<QueryKey, number>): Promise<Data> => {
        const params = queryParams ? queryParams : new URLSearchParams();
        const pageParamKeyName = pageParamOptions?.pageParamName ?? "page";

        // The default "page" param and this GET branch exist only for the deprecated V3
        // feed query (useFeedInfiniteQuery), the last caller relying on the default "page"
        // URL param. They can be removed once that query is gone (COM-4610).
        if (method !== HttpMethod.POST) {
            if (params.has(pageParamKeyName)) {
                params.delete(pageParamKeyName);
            }
            params.append(pageParamKeyName, String(pageParam));
        }

        const url = buildQueryUrl(locale, _url, params);
        const requestBody = typeof body === "function" ? body(pageParam) : body;
        const response = await fetch(url.toLowerCase(), {
            ...(requestBody !== undefined && { body: requestBody }),
            ...(method !== undefined && { method }),
            headers: {
                Accept: "application/json",
                ...(method === HttpMethod.POST && { "Content-Type": "application/json" }),
            },
        });

        const responseValue: Data = await response.json();
        if (!response.ok) {
            throw responseValue;
        }

        return responseValue;
    };

export interface IInfiniteQueryOptions<Data, Error>
    extends Omit<
        UseInfiniteQueryOptions<Data, Error, InfiniteData<Data, number>, QueryKey, number>,
        "queryKey" | "initialPageParam" | "getNextPageParam"
    > {
    queryParams?: URLSearchParams;
    body?: string | TBodyGenerator;
    method?: THttpMethod;
    getNextPageParam?: (lastPage: Data, allPages: Data[], lastPageParam: number, allPageParams: number[]) => number | undefined | null;
}

type TPageParamOptions = {
    pageParamName: string;
    defaultPageParamNumber: number;
};

export const useInfiniteQuery = <Data = unknown, Error = unknown>(
    _url: string,
    options: IInfiniteQueryOptions<Data, Error>,
    pageParamOptions?: TPageParamOptions,
    queryKey?: QueryKey
) => {
    const { language, country } = useRouterLocale();
    const retryOptions = options?.method === HttpMethod.POST ? {} : { retry: MAX_ATTEMPTS, retryDelay: RETRY_DELAY_MS };
    const initialPageParam = pageParamOptions?.defaultPageParamNumber ?? 1;
    const { queryParams, body, method, getNextPageParam, ...queryOptions } = options;

    return _useInfiniteQuery<Data, Error, InfiniteData<Data, number>, QueryKey, number>({
        queryKey: queryKey ? queryKey : [_url],
        queryFn: getQueryFn<Data>({
            _url,
            locale: { language, country },
            queryParams,
            pageParamOptions,
            body,
            method,
        }),
        initialPageParam,
        getNextPageParam: getNextPageParam!,
        ...retryOptions,
        ...queryOptions,
    });
};
